import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { 
  PlayCircle, PauseCircle, CheckCircle, Clock, Dumbbell, 
  Activity, Target, AlertTriangle, ChevronRight, Camera, Video
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import DashboardLayout from '../components/DashboardLayout';
import PoseCameraAdvanced from '../components/PoseCameraAdvanced';
import PoseCameraErrorBoundary from '../components/PoseCameraErrorBoundary';
import BodyMapSVG from '../components/BodyMapSVG';
import JointDiagram from '../components/JointDiagram';
import FormCheckControl from '../components/FormCheckControl';
import GuidedExercisePanel from '../components/GuidedExercisePanel';
import AnalyzerReport from '../components/AnalyzerReport';
import { getMusclesForExercise } from '../utils/exerciseMuscles';
import { scoreBucket, toneClass, toneHex, formatTempo } from '../utils/coachLanguage';
import { jointDescription, jointTriplet } from '../utils/jointDescription';
import api from '../services/api';
import { toast } from 'sonner';

// Angle status → plain-English (only for angles that DO have a target; others are hidden).
const angleStatusText = (s) => {
  if (s === 'good') return 'in range';
  if (s === 'warning') return 'close';
  if (s === 'off') return 'out of range';
  return '';
};

// Camera view → human label used on movement cards + filtering logic.
const viewLabel = (v) => {
  if (v === 'front') return 'Front view';
  if (v === 'side') return 'Side view';
  if (v === 'angled') return 'Angled view';
  return 'Camera view unknown';
};

// How the live session should present a movement. Prefers the backend's
// `tracking_mode` on the definition; falls back to the same deterministic rules
// so it's correct even before the backend has tagged an older definition.
//   'reps'   → live camera + rep counter + form-check upload (countable lift)
//   'timed'  → countdown timer + skeleton mirror (a hold / isometric)
//   'follow' → target + Done + skeleton mirror (rotational / mobility / warm-up)
const FOLLOW_RE = /\b(circle|circles|circular|rotation|rotate|rotating|swing|swings|swinging|sweep|sweeps|shake|wrist|ankle|neck roll|hip circle|arm circle|shoulder roll|jog|jogging|march|marching|skip|high knee|butt kick|inchworm|cat[\s-]*cow|flow|dynamic|mobility|pendulum|windmill|leg swing|arm swing|jumping jack)\b/i;
const HOLD_RE = /\b(hold|holds|plank|wall sit|wall-sit|isometric|hollow|dead hang|l-sit|superman hold|bridge hold|side plank|static|balance|stand on one|single-leg stand|wall angel hold)\b/i;
// Countable lifts that ALWAYS deserve the live rep-counter + form analyser,
// even when the backend definition is missing/incomplete. (Holds & mobility
// are matched first above, so e.g. "bridge hold" stays a timer.)
const REPS_RE = /\b(push[\s-]?ups?|pull[\s-]?ups?|chin[\s-]?ups?|squats?|lunges?|deadlifts?|rows?|bench(\s*press)?|overhead\s*press|shoulder\s*press|military\s*press|press|presses|curls?|dips?|sit[\s-]?ups?|crunch(es)?|glute\s*bridges?|hip\s*thrusts?|calf\s*raises?|lateral\s*raises?|front\s*raises?|leg\s*raises?|pull[\s-]?downs?|leg\s*press|step[\s-]?ups?|thrusters?|cleans?|snatch(es)?|raises?|extensions?|flys?|flyes?)\b/i;
const getTrackingMode = (def, exercise) => {
  if (def?.tracking_mode) return def.tracking_mode;
  const name = (exercise?.name || '').toLowerCase();
  const reps = String(exercise?.reps || exercise?.reps_or_seconds || '');
  if (FOLLOW_RE.test(name)) return 'follow';
  if (HOLD_RE.test(name) || /\b(sec|second|min|minute|hold)s?\b/i.test(reps)) return 'timed';
  // Known countable lift → always show the form analyser, even with no backend def.
  if (REPS_RE.test(name)) return 'reps';
  const pm = def?.primary_motion;
  if (pm?.landmark_triplet?.length === 3 && pm.start_range && pm.bottom_range) {
    const amp = Math.abs((pm.start_range[0] + pm.start_range[1]) / 2 - (pm.bottom_range[0] + pm.bottom_range[1]) / 2);
    if (amp >= 20) return 'reps';
  }
  return 'follow';
};


const WorkoutSession = () => {
  const { sessionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [session, setSession] = useState(location.state?.session || null);
  const [workout, setWorkout] = useState(location.state?.workout || null);
  // Hydrated = we've finished fetching session+workout on cold load.
  // While false, we show a skeleton so we never flash the active-workout UI
  // for a completed session (the nav glitch the user spotted).
  const [hydrated, setHydrated] = useState(!!(location.state?.session && location.state?.workout));
  const [currentExercise, setCurrentExercise] = useState(0);
  const [currentSet, setCurrentSet] = useState(1);
  const [isActive, setIsActive] = useState(true);
  const [completed, setCompleted] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  // Pose detection + live feedback are ON by default. Toggles removed in
  // iter68 — wellness users don't tune feature flags; they want to start.
  const usePoseDetection = true;
  const showLiveFeedback = true;
  const [currentSetMetrics, setCurrentSetMetrics] = useState([]);
  const [setRepCount, setSetRepCount] = useState(0);
  const metricsRef = useRef([]);
  
  // Per-rep form data collection for degradation-based fatigue
  const exerciseFormDataRef = useRef({}); // { exerciseName: [{ rep_num, peak_depth, symmetry, ... }] }
  // Per-set summary data: { exerciseName: [ {setNum, reps: [...]} ] }
  const setPathDataRef = useRef({});
  
  // Live fatigue state for body map during workout
  const [liveFatigue, setLiveFatigue] = useState({}); // { muscleKey: { color, recovery_pct } }
  
  // Exercise definitions (how-to, form standards, video, rep detection)
  const [exerciseDefs, setExerciseDefs] = useState({}); // { exerciseNameLower: definition }
  const [showHowTo, setShowHowTo] = useState(false);

  // Optional per-exercise Form Check (server-side pose) results, keyed by
  // exercise index → array of per-clip results (one per captured view, so a
  // dual-angle move like a squat can hold both a side + front check). Lifted
  // here so the camera-card card + the exercise-list row stay in sync. Backend
  // also persists these onto summary.form_checks.
  const [formChecks, setFormChecks] = useState({});
  const handleFormCheckResult = useCallback((idx, clip) => {
    setFormChecks(prev => {
      const arr = Array.isArray(prev[idx]) ? prev[idx].slice() : [];
      const vi = arr.findIndex(c => c.view_detected === clip.view_detected);
      if (vi >= 0) arr[vi] = clip; else arr.push(clip);
      return { ...prev, [idx]: arr };
    });
  }, []);

  // Immersive workout mode — auto-enters fullscreen-like layout when camera starts.
  // Hides dashboard chrome, expands camera, keeps Live Fatigue + Exercise List on the right rail.
  const [immersive, setImmersive] = useState(false);
  const handleCameraStateChange = useCallback((active) => {
    setImmersive(active);
  }, []);

  // iOS PWA/Safari fix: while the immersive full-screen camera overlay is open,
  // lock the background page so it can't scroll behind the fixed overlay. Without
  // this, the page underneath scrolls during the session and stays shifted-up /
  // clipped after the camera is closed. We snap to top on enter AND exit so the
  // workout view always renders from the top when the user returns.
  useEffect(() => {
    if (!immersive) return;
    const { body, documentElement: html } = document;
    const prevBody = body.style.overflow;
    const prevHtml = html.style.overflow;
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    window.scrollTo(0, 0);
    return () => {
      body.style.overflow = prevBody;
      html.style.overflow = prevHtml;
      window.scrollTo(0, 0);
    };
  }, [immersive]);

  useEffect(() => {
    // Always fetch session data when sessionId changes
    fetchSessionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Persist in-progress position so navigating away + back resumes the session.
  // Guarded so the initial mount (currentExercise=0) can't overwrite the saved
  // value BEFORE fetchSessionData has restored it.
  const progressHydratedRef = useRef(false);
  useEffect(() => {
    if (!sessionId || completed || !progressHydratedRef.current) return;
    try {
      localStorage.setItem(`ws_progress_${sessionId}`, JSON.stringify({ currentExercise, currentSet }));
    } catch (e) { /* ignore */ }
  }, [sessionId, completed, currentExercise, currentSet]);

  // Fetch exercise definitions in the BACKGROUND once the workout is known.
  // Skipped for completed sessions (review mode never needs camera angles).
  useEffect(() => {
    const fetchDefs = async () => {
      if (workout?.id && !completed && Object.keys(exerciseDefs).length === 0) {
        try {
          const defsRes = await api.getExerciseDefinitions(workout.id);
          const defsMap = {};
          const wxNames = (workout?.exercises || []).map(e => e.name?.toLowerCase());
          for (let i = 0; i < (defsRes.data || []).length; i++) {
            const d = defsRes.data[i];
            if (!d) continue;
            if (d.name_lower) defsMap[d.name_lower] = d;
            if (wxNames[i]) defsMap[wxNames[i]] = d;
          }
          setExerciseDefs(defsMap);
        } catch (e) {
          console.warn('No exercise definitions available:', e);
        }
      }
    };
    fetchDefs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workout?.id, completed]);

  useEffect(() => {
    let interval;
    if (isActive && !completed) {
      interval = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isActive, completed]);

  const fetchSessionData = async () => {
    try {
      const response = await api.getSession(sessionId);
      setSession(response.data);
      const isCompleted = !!response.data.completed_at;
      let workoutData = null;
      if (response.data.workout_plan_id) {
        try {
          const workoutRes = await api.getWorkout(response.data.workout_plan_id);
          workoutData = workoutRes.data;
          setWorkout(workoutData);
          // NOTE: exercise definitions (form standards + ideal angles for the
          // camera) are NOT fetched here — they used to block this load with
          // one LLM call per exercise. They now load quietly in the background
          // effect below, and are skipped entirely for completed sessions.
        } catch (e) {
          console.warn('Workout plan fetch failed:', e);
          // Always render SOMETHING — never let workout stay null.
          workoutData = {
            id: response.data.workout_plan_id,
            name: response.data.workout_name || (isCompleted ? 'Completed Workout' : 'Workout'),
            exercises: response.data.exercises || [],
          };
          setWorkout(workoutData);
        }
      } else {
        // No workout_plan_id (e.g., chat-clip quick-log session)
        workoutData = {
          name: response.data.workout_name || 'Workout',
          exercises: response.data.exercises || [],
        };
        setWorkout(workoutData);
      }
      if (isCompleted) {
        setCompleted(true);
        setAnalysis(response.data.summary || null);
      }
      // Hydrate saved Form Check scores so they persist across navigation /
      // reload (the backend stores them on summary.form_checks).
      const fcs = response.data.summary?.form_checks;
      if (Array.isArray(fcs) && fcs.length) {
        const map = {};
        fcs.forEach((c) => {
          const i = c.exercise_index;
          if (i === null || i === undefined) return;
          if (!map[i]) map[i] = [];
          map[i].push({
            score: c.score, headline: c.headline,
            view_detected: c.view_detected, requested_view: c.requested_view,
            view_mismatch: c.view_mismatch,
            sessions_analysis: c.sessions_analysis,
            exercise_name: c.exercise_name, movement_label: c.movement_label,
          });
        });
        setFormChecks(map);
      }
      // Restore in-progress position so leaving the tab + returning resumes
      // where you were instead of snapping back to exercise 1.
      if (!isCompleted) {
        try {
          const saved = JSON.parse(localStorage.getItem(`ws_progress_${sessionId}`) || 'null');
          const exCount = (workoutData?.exercises || []).length;
          if (saved && Number.isInteger(saved.currentExercise) && exCount > 0) {
            setCurrentExercise(Math.min(Math.max(0, saved.currentExercise), exCount - 1));
            setCurrentSet(Number.isInteger(saved.currentSet) && saved.currentSet > 0 ? saved.currentSet : 1);
          }
        } catch (e) { /* ignore */ }
      }
    } catch (error) {
      console.error('Session load failed:', error);
      toast.error('Failed to load session');
      // Render fallback shell so user has a back button instead of blank screen
      setWorkout({ name: 'Could not load session', exercises: [] });
      setCompleted(true);
    } finally {
      progressHydratedRef.current = true;
      setHydrated(true);
    }
  };

  const handleComplete = async () => {
    setAnalyzing(true);
    try {
      // Build form data from collected per-rep metrics
      const formData = { exercises: [] };
      const formEntries = exerciseFormDataRef.current;
      for (const [name, reps] of Object.entries(formEntries)) {
        if (reps.length > 0) {
          formData.exercises.push({ exercise_name: name, reps });
        }
      }
      
      // Send form data with completion if we have camera data
      const hasFormData = formData.exercises.length > 0;
      const response = hasFormData
        ? await api.post(`/sessions/${sessionId}/complete`, formData)
        : await api.post(`/sessions/${sessionId}/complete`);
      
      setSession(response.data);
      setAnalysis(response.data.summary || null);
      setCompleted(true);
      setIsActive(false);
      setImmersive(false);
      try { localStorage.removeItem(`ws_progress_${sessionId}`); } catch (e) { /* ignore */ }
      
      const method = response.data?.summary?.fatigue_method;
      if (method === 'camera_degradation') {
        toast.success('Workout completed! Fatigue calculated from your actual movement.');
      } else {
        toast.success('Workout completed! Analysis ready.');
      }
    } catch (error) {
      toast.error('Failed to complete workout');
    } finally {
      setAnalyzing(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFinishSet = async () => {
    const ex = exercises[currentExercise];
    if (!ex) return;
    const totalSets = ex.sets || 3;

    // Store set data summary
    const exName = ex.name;
    if (!setPathDataRef.current[exName]) setPathDataRef.current[exName] = [];
    const setReps = exerciseFormDataRef.current[exName] || [];
    setPathDataRef.current[exName].push({
      setNum: currentSet,
      reps: setReps.filter(r => r.set_num === currentSet || !r.set_num),
    });

    if (currentSet < totalSets) {
      setCurrentSet(prev => prev + 1);
      toast.success(`Set ${currentSet} complete! Start set ${currentSet + 1}.`, { duration: 4000 });
    }
  };

  const handleNextExercise = () => {
    setCurrentExercise(prev => prev + 1);
    setCurrentSet(1);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#CCFF00';
    if (score >= 60) return '#F59E0B';
    return '#EF4444';
  };

  if (!hydrated || !workout) {
    return (
      <DashboardLayout>
        <div className="p-6 flex flex-col items-center justify-center min-h-[60vh]">
          <Activity className="w-12 h-12 text-primary animate-pulse mb-4" />
          <p className="text-zinc-400 mb-6">Loading session...</p>
          <Button
            variant="outline"
            onClick={() => navigate('/sessions')}
            data-testid="loading-back-btn"
          >
            ← Back to sessions
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const exercises = workout.exercises || [];
  const progress = ((currentExercise + 1) / exercises.length) * 100;

  return (
    <DashboardLayout>
      <div className={immersive ? '' : 'p-6 space-y-6'}>
        {/* Defensive back button — ALWAYS visible (even in immersive mode) so the
            user can never get trapped on a broken/black screen.
            Top-right floating to avoid layout shift. */}
        {completed && (
          <button
            onClick={() => navigate('/sessions')}
            className="fixed top-4 right-4 z-[100] hpme-glass px-3 py-1.5 rounded-sm text-xs text-zinc-300 hover:text-white border border-zinc-700 hover:border-primary transition-colors flex items-center gap-1.5"
            data-testid="session-back-btn"
          >
            ← Sessions
          </button>
        )}

        {/* Header — hidden during immersive workout mode. */}
        {!immersive && (
        <div className="flex items-center justify-between">
          <div>
            <p className={`hpme-label mb-1 ${completed ? 'text-green-500' : 'text-primary'}`} data-testid="session-status-label">
              {completed ? 'COMPLETED SESSION' : 'ACTIVE SESSION'}
            </p>
            <h1 className="text-2xl md:text-3xl font-secondary font-bold tracking-tight">
              {workout.name}
            </h1>
          </div>
          <div className="hpme-glass px-4 py-2 rounded-sm flex items-center gap-3" data-testid="session-duration">
            <Clock className={`w-5 h-5 ${completed ? 'text-green-500' : 'text-primary'}`} />
            <span className="hpme-number text-2xl">
              {completed
                ? formatTime(analysis?.duration_seconds || 0)
                : formatTime(elapsedTime)}
            </span>
          </div>
        </div>
        )}

        {/* Progress / Completion summary chip — hidden in immersive (lives inside slim header instead) */}
        {!immersive && !completed && (
          <div className="hpme-card p-4">
            <div className="hpme-card-corner" />
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-zinc-400">Workout Progress</span>
              <span className="text-sm text-zinc-400">{currentExercise + 1} / {exercises.length}</span>
            </div>
            <Progress value={progress} className="h-2 bg-zinc-800" />
          </div>
        )}
        {!immersive && completed && (
          <div className="hpme-card p-4 flex items-center gap-3" data-testid="session-completion-chip">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
            <span className="text-sm text-zinc-300">
              {analysis?.total_reps_captured > 0
                ? `Completed · ${analysis.exercises_captured} exercise${analysis.exercises_captured === 1 ? '' : 's'} · ${analysis.total_reps_captured} reps tracked`
                : 'Completed · no camera reps this session'}
            </span>
          </div>
        )}

        {!completed ? (
          <div className={immersive
            ? 'fixed inset-0 z-50 bg-black flex flex-col lg:grid lg:grid-cols-[1fr_360px] overflow-hidden safe-top safe-bottom'
            : 'grid grid-cols-1 lg:grid-cols-3 gap-6'
          } data-testid={immersive ? 'immersive-workout-mode' : 'workout-active-grid'}>
            {/* Current Exercise */}
            <div className={immersive ? 'flex flex-col min-h-0 flex-1 overflow-hidden p-2 sm:p-3' : 'lg:col-span-2'}>
              <div className={immersive ? 'flex flex-col min-h-0 flex-1' : 'hpme-card p-6'}>
                {!immersive && <div className="hpme-card-corner" />}
                {!immersive && <p className="hpme-label mb-2">CURRENT EXERCISE</p>}
                
                {exercises[currentExercise] && (
                  <>
                    {immersive ? (
                      /* Slim header for immersive mode — fits in one row, frees up space for the camera. */
                      <div className="flex items-center justify-between mb-3 pb-3 border-b border-zinc-800" data-testid="immersive-header">
                        <div className="flex items-baseline gap-3 min-w-0">
                          <h2 className="text-xl font-secondary font-bold tracking-tight truncate">
                            {exercises[currentExercise].name}
                          </h2>
                          <span className="text-xs text-zinc-400 whitespace-nowrap">
                            Set <span className="text-primary font-bold">{currentSet}</span> / {exercises[currentExercise].sets || 3}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-zinc-500 whitespace-nowrap">Workout {currentExercise + 1}/{exercises.length}</span>
                          <div className="w-20 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                            <div className="h-full bg-primary" style={{ width: `${progress}%` }} />
                          </div>
                          <span className="hpme-number text-sm text-primary whitespace-nowrap" data-testid="immersive-timer">{formatTime(elapsedTime)}</span>
                        </div>
                      </div>
                    ) : (
                      <>
                        <h2 className="text-3xl font-secondary font-bold tracking-tight mb-1">
                          {exercises[currentExercise].name}
                        </h2>
                        <p className="text-zinc-400 text-sm mb-4" data-testid="set-indicator">
                          Set <span className="text-primary font-bold">{currentSet}</span> of {exercises[currentExercise].sets || 3}
                        </p>
                      </>
                    )}

                    {/* Exercise How-To & Reference — hidden in immersive mode (workout focus). */}
                    {!immersive && (() => {
                      const def = exerciseDefs[exercises[currentExercise]?.name?.toLowerCase()];
                      if (!def || def.generated_by === 'none') return null;
                      return (
                        <div className="mb-4" data-testid="exercise-info-panel">
                          <button 
                            onClick={() => setShowHowTo(prev => !prev)}
                            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors mb-2"
                            data-testid="how-to-toggle"
                          >
                            <ChevronRight className={`w-4 h-4 transition-transform ${showHowTo ? 'rotate-90' : ''}`} />
                            <span>How To Perform</span>
                            {def.video_query && (
                              <a 
                                href={`https://www.youtube.com/results?search_query=${encodeURIComponent(def.video_query)}`}
                                target="_blank" rel="noopener noreferrer"
                                onClick={e => e.stopPropagation()}
                                className="ml-auto flex items-center gap-1 text-xs text-red-400 hover:text-red-300"
                                data-testid="video-tutorial-link"
                              >
                                <Video className="w-3.5 h-3.5" />
                                Watch Tutorial
                              </a>
                            )}
                          </button>
                          {showHowTo && (
                            <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-sm p-3 space-y-3 animate-slide-up">
                              <p className="text-zinc-300 text-sm whitespace-pre-line leading-relaxed">{def.how_to}</p>
                              {def.ideal_angles && Object.keys(def.ideal_angles).length > 0 && (
                                <div className="border-t border-zinc-700/50 pt-2">
                                  <p className="text-xs text-zinc-500 uppercase mb-1.5">Ideal Angles</p>
                                  <div className="flex flex-wrap gap-2">
                                    {Object.entries(def.ideal_angles).map(([name, info]) => (
                                      <div key={name} className="bg-zinc-900/60 border border-zinc-700/30 px-2 py-1 rounded-sm">
                                        <span className="text-xs text-zinc-400">{name}: </span>
                                        <span className="text-xs text-primary font-mono">{info.ideal}{info.unit ? `${info.unit}` : ''}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {def.rep_detection?.description && (
                                <div className="border-t border-zinc-700/50 pt-2">
                                  <p className="text-xs text-zinc-500 uppercase mb-1">Rep Detection</p>
                                  <p className="text-xs text-zinc-400">{def.rep_detection.description}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    
                    <div className={immersive ? 'hidden' : 'grid grid-cols-3 gap-4 mb-6'}>
                      <div className="bg-zinc-800/50 p-4 rounded-sm text-center">
                        <p className="hpme-number text-3xl text-primary">{exercises[currentExercise].sets}</p>
                        <p className="text-xs text-zinc-500 uppercase mt-1">Sets</p>
                      </div>
                      <div className="bg-zinc-800/50 p-4 rounded-sm text-center">
                        <p className="hpme-number text-3xl text-accent-blue">{exercises[currentExercise].reps}</p>
                        <p className="text-xs text-zinc-500 uppercase mt-1">Reps</p>
                      </div>
                      <div className="bg-zinc-800/50 p-4 rounded-sm text-center">
                        <p className="hpme-number text-3xl text-accent-purple">{exercises[currentExercise].rest_seconds}s</p>
                        <p className="text-xs text-zinc-500 uppercase mt-1">Rest</p>
                      </div>
                    </div>

                    {/* "COACH TIP" card removed in iter68b — wellness redesign.
                        Coaching tips now flow through Nikki's chat (pre-session
                        whisper + live drivers on /performance) rather than as a
                        cold static box above the camera. The exercise's own
                        `notes` field is still available via the workout summary
                        post-session if relevant. */}

                    {/* Live coaching is ON by default — wellness users don't tune
                        feature flags. If they want to opt out, they can use the
                        upload mode or skip the camera entirely from a different
                        flow. Toggles removed in iter68 to reduce visual noise. */}

                    {(() => {
                      const _def = exerciseDefs[exercises[currentExercise]?.name?.toLowerCase()] || null;
                      const _mode = getTrackingMode(_def, exercises[currentExercise]);
                      // Non-countable movements (holds, rotations, mobility flows)
                      // get a guided panel (timer or follow-along) + a skeleton
                      // mirror — NEVER a rep counter or form-check upload.
                      if (_mode !== 'reps') {
                        return (
                          <GuidedExercisePanel
                            mode={_mode}
                            exercise={exercises[currentExercise]}
                            exerciseName={exercises[currentExercise].name}
                            exerciseDefinition={_def}
                            immersive={immersive}
                            onCameraStateChange={handleCameraStateChange}
                          />
                        );
                      }
                      return (
                        <>
                    {/* Live Camera Mode — trackable rep exercises only */}
                    {usePoseDetection && (
                      <div className={immersive ? 'flex-1 min-h-0 flex flex-col' : ''}>
                      <PoseCameraErrorBoundary>
                        <PoseCameraAdvanced 
                          immersive={immersive}
                          exerciseName={exercises[currentExercise].name}
                          exerciseDefinition={exerciseDefs[exercises[currentExercise]?.name?.toLowerCase()] || null}
                          setNumber={currentSet}
                          showLiveFeedback={showLiveFeedback}
                          liveFatigue={liveFatigue}
                          onCameraStateChange={handleCameraStateChange}
                          onMetricsUpdate={(metrics) => {
                            metricsRef.current.push(metrics);
                            setCurrentSetMetrics(prev => [...prev.slice(-10), metrics]);
                          }}
                          onRepComplete={(count, analysisData) => {
                            setSetRepCount(count);
                            // Collect per-rep form data for degradation analysis
                            const exerciseName = exercises[currentExercise]?.name;
                            if (exerciseName && analysisData) {
                              if (!exerciseFormDataRef.current[exerciseName]) {
                                exerciseFormDataRef.current[exerciseName] = [];
                              }
                              exerciseFormDataRef.current[exerciseName].push({
                                rep_num: count,
                                set_num: currentSet,
                                peak_depth: analysisData.depthPct || 0,
                                symmetry: analysisData.symmetry || null,
                                joint_angles: analysisData.jointAngles || {},
                                camera_view: analysisData.cameraView || null,
                                rep_duration: analysisData.repDurationSec ?? (analysisData.repDuration ? analysisData.repDuration / 1000 : null),
                                form_score: analysisData.formScore || null,
                                fatigue_signal: analysisData.fatigueSignal || null,
                                timestamp: Date.now()
                              });
                              
                              // Update live fatigue body map using REAL movement degradation
                              const muscles = getMusclesForExercise(exerciseName);
                              const realFatigueScore = analysisData.fatigueScore || 0;
                              setLiveFatigue(prev => {
                                const updated = { ...prev };
                                const getColor = (pct) => {
                                  if (pct >= 85) return '#22C55E';
                                  if (pct >= 70) return '#4ADE80';
                                  if (pct >= 55) return '#FDE047';
                                  if (pct >= 40) return '#FB923C';
                                  if (pct >= 25) return '#F87171';
                                  return '#EF4444';
                                };
                                // Primary muscles: use real fatigue score from movement degradation
                                (muscles.primary || []).forEach(m => {
                                  const recovery = Math.max(10, 100 - realFatigueScore);
                                  updated[m] = { recovery_pct: recovery, color: getColor(recovery), source_exercises: [exerciseName] };
                                });
                                // Secondary muscles: half the fatigue of primary
                                (muscles.secondary || []).forEach(m => {
                                  const recovery = Math.max(20, 100 - (realFatigueScore * 0.5));
                                  const existing = prev[m];
                                  if (!existing || existing.recovery_pct > recovery) {
                                    updated[m] = { recovery_pct: recovery, color: getColor(recovery), source_exercises: [exerciseName] };
                                  }
                                });
                                return updated;
                              });
                            }
                            toast.success(`Rep ${count} complete!`, { duration: 1000 });
                          }}
                        />
                      </PoseCameraErrorBoundary>
                      </div>
                    )}

                    {/* Live Camera disabled placeholder */}
                    {!usePoseDetection && (
                      <div className="bg-zinc-900 border border-zinc-800 rounded-sm aspect-video flex items-center justify-center relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent" />
                        <div className="text-center z-10">
                          <Camera className="w-16 h-16 text-zinc-500 mx-auto mb-4" />
                          <p className="text-zinc-400">Camera is disabled</p>
                          <p className="text-xs text-zinc-500 mt-2">Enable AI Detection above</p>
                        </div>
                      </div>
                    )}

                    {/* Optional in-session Form Check — upload a clip, get a
                        server-scored technique read. Never blocks finishing. */}
                    {!immersive && exercises[currentExercise] && (
                      <FormCheckControl
                        exerciseName={exercises[currentExercise].name}
                        exerciseIndex={currentExercise}
                        sessionId={sessionId}
                        result={formChecks[currentExercise]}
                        onResult={handleFormCheckResult}
                        variant="full"
                      />
                    )}
                        </>
                      );
                    })()}

                    <div className={immersive ? 'flex gap-3 mt-3 flex-shrink-0' : 'flex gap-4 mt-6'}>
                      {currentExercise > 0 && currentSet === 1 && (
                        <Button 
                          className="hpme-btn-secondary"
                          onClick={() => { setCurrentExercise(prev => prev - 1); setCurrentSet(1); }}
                          data-testid="prev-exercise-btn"
                        >
                          Previous
                        </Button>
                      )}
                      
                      {/* FINISH SET button */}
                      {currentSet < (exercises[currentExercise]?.sets || 3) && (
                        <Button 
                          className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white font-semibold py-2 px-4 rounded-sm"
                          onClick={handleFinishSet}
                          data-testid="finish-set-btn"
                        >
                          Finish Set {currentSet} <ChevronRight className="w-5 h-5 ml-2" />
                        </Button>
                      )}

                      {/* NEXT EXERCISE (only after all sets) */}
                      {currentSet >= (exercises[currentExercise]?.sets || 3) && currentExercise < exercises.length - 1 && (
                        <Button 
                          className="hpme-btn-primary flex-1"
                          onClick={handleNextExercise}
                          data-testid="next-exercise-btn"
                        >
                          Next Exercise <ChevronRight className="w-5 h-5 ml-2" />
                        </Button>
                      )}

                      {/* COMPLETE WORKOUT (last exercise, last set) */}
                      {currentSet >= (exercises[currentExercise]?.sets || 3) && currentExercise >= exercises.length - 1 && (
                        <Button 
                          className="hpme-btn-primary flex-1"
                          onClick={handleComplete}
                          disabled={analyzing}
                          data-testid="complete-workout-btn"
                        >
                          {analyzing ? 'Analyzing...' : 'Complete Workout'}
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Sidebar: Live Fatigue + Exercise List */}
            <div className={immersive ? 'hidden lg:block border-l border-zinc-800 bg-black p-3 space-y-3 overflow-y-auto' : 'space-y-4'} data-testid="workout-right-rail">
              {/* Live Fatigue Body Map */}
              <div className={immersive ? 'border border-zinc-800 rounded-sm p-3 bg-zinc-900/40' : 'hpme-card p-4'}>
                {!immersive && <div className="hpme-card-corner" />}
                <p className="hpme-label mb-2">LIVE MUSCLE FATIGUE</p>
                <BodyMapSVG
                  muscleData={liveFatigue}
                  highlightMode="recovery"
                  size="sm"
                  showLabels={Object.keys(liveFatigue).length > 0}
                  interactive={true}
                />
                {Object.keys(liveFatigue).length === 0 && (
                  <p className="text-zinc-500 text-xs text-center mt-2">
                    Complete reps to see fatigue build
                  </p>
                )}
              </div>

              {/* Exercise List */}
              <div className={immersive ? 'border border-zinc-800 rounded-sm p-3 bg-zinc-900/40' : 'hpme-card p-6'}>
                {!immersive && <div className="hpme-card-corner" />}
              <p className="hpme-label mb-4">EXERCISE LIST</p>
              <div className={immersive ? 'space-y-2 max-h-[calc(100vh-360px)] overflow-y-auto' : 'space-y-2 max-h-[500px] overflow-y-auto'}>
                {exercises.map((ex, idx) => (
                  <div key={idx} data-testid={`exercise-row-${idx}`}>
                    <button
                      onClick={() => setCurrentExercise(idx)}
                      className={`w-full text-left p-3 rounded-sm transition-colors ${
                        idx === currentExercise
                          ? 'bg-primary/20 border border-primary/30'
                          : idx < currentExercise
                          ? 'bg-green-500/10 border border-green-500/20'
                          : 'bg-zinc-800/50 hover:bg-zinc-800'
                      }`}
                      data-testid={`exercise-${idx}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                          idx < currentExercise ? 'bg-green-500 text-black' : 
                          idx === currentExercise ? 'bg-primary text-black' : 'bg-zinc-700'
                        }`}>
                          {idx < currentExercise ? <CheckCircle className="w-4 h-4" /> : idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm truncate">{ex.name}</p>
                          <p className="text-xs text-zinc-500">
                            {idx === currentExercise 
                              ? `Set ${currentSet}/${ex.sets || 3} • ${ex.reps} reps`
                              : `${ex.sets || 3} × ${ex.reps}`
                            }
                          </p>
                        </div>
                      </div>
                    </button>
                    {getTrackingMode(exerciseDefs[ex.name?.toLowerCase()] || null, ex) === 'reps' && (
                      <FormCheckControl
                        exerciseName={ex.name}
                        exerciseIndex={idx}
                        sessionId={sessionId}
                        result={formChecks[idx]}
                        onResult={handleFormCheckResult}
                        variant="compact"
                      />
                    )}
                  </div>
                ))}
              </div>
              </div>
            </div>
          </div>
        ) : (
          /* Analysis Results — one overall, one coach paragraph, one card per movement. */
          <div className="space-y-6">
            {(() => {
              const overall = (analysis?.overall_score !== null && analysis?.overall_score !== undefined)
                ? Math.round(analysis.overall_score) : null;
              const overallBk = scoreBucket(overall);
              return (
                <div className="hpme-card p-6 border-l-4" style={{ borderLeftColor: overall !== null ? toneHex(overallBk.tone) : '#3f3f46' }}>
                  <div className="hpme-card-corner" />
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                    <div>
                      <h2 className="text-2xl font-secondary font-bold tracking-tight">WORKOUT COMPLETE</h2>
                      <p className="text-zinc-400 text-sm">
                        {analysis?.total_reps_captured > 0
                          ? 'Measured from your camera data'
                          : 'Session saved — add camera tracking next time for detailed analysis'}
                      </p>
                    </div>
                  </div>
                  {overall !== null && (
                    <div className="flex items-baseline gap-3" data-testid="summary-overall">
                      <p className="hpme-label">OVERALL</p>
                      <p className={`font-secondary font-bold text-3xl ${toneClass(overallBk.tone)}`}>
                        {overallBk.label}
                        {' '}
                        <span className="text-zinc-400 font-mono text-lg ml-1">{overall}/100</span>
                      </p>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* In-session Form Check — analyzed in-browser with the SAME engine
                as the live camera, so the report here is identical to live. */}
            {Array.isArray(analysis?.form_checks) && analysis.form_checks.length > 0 && (
              <div className="space-y-4" data-testid="form-check-summary">
                <div className="flex items-center justify-between">
                  <h3 className="font-secondary font-bold text-xl flex items-center gap-2">
                    <Video className="w-5 h-5 text-accent-purple" /> FORM CHECK
                  </h3>
                  {analysis.form_score != null && (
                    <span
                      className={`font-secondary font-bold ${toneClass(scoreBucket(analysis.form_score).tone)}`}
                      data-testid="form-check-summary-score"
                    >
                      {scoreBucket(analysis.form_score).label}
                      {' '}
                      <span className="text-zinc-400 font-mono text-sm ml-1">{Math.round(analysis.form_score)}/100</span>
                    </span>
                  )}
                </div>
                {analysis.form_checks.map((c, i) => (
                  c.sessions_analysis ? (
                    <div key={i} data-testid={`form-check-report-${i}`}>
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                        {c.exercise_name || 'Exercise'}{c.view_detected ? ` · ${c.view_detected} view` : ''}
                      </p>
                      <AnalyzerReport
                        analysis={{
                          movement_label: c.exercise_name,
                          metrics: { view_detected: c.view_detected, rep_count: c.sessions_analysis?.rep_count },
                          sessions_analysis: c.sessions_analysis,
                        }}
                      />
                    </div>
                  ) : (
                    <div
                      key={i}
                      className="flex items-center justify-between gap-3 text-sm bg-zinc-800/40 rounded-sm px-3 py-2"
                      data-testid={`form-check-summary-row-${i}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-zinc-200 font-medium truncate">{c.exercise_name || 'Exercise'}</span>
                        {c.view_detected && (
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-500/30">
                            {c.view_detected}
                          </span>
                        )}
                      </div>
                      <span className={`font-secondary font-bold whitespace-nowrap ${toneClass(scoreBucket(c.score).tone)}`}>
                        {scoreBucket(c.score).label}
                        {c.score != null && <span className="text-zinc-500 font-mono text-xs ml-2">{Math.round(c.score)}/100</span>}
                      </span>
                    </div>
                  )
                ))}
              </div>
            )}

            {/* Empty-state card — shown ONLY when no camera reps captured. */}
            {analysis && analysis.total_reps_captured === 0 && (
              <div className="hpme-card p-6 border-l-4 border-l-zinc-600" data-testid="empty-session-card">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-zinc-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-secondary font-bold text-lg mb-1">No camera reps captured</h3>
                    <p className="text-zinc-400 text-sm">
                      The camera didn't pick up any full reps this session. Next time: step fully into frame, film from the side for pushups / squats (so we can see depth), or from the front for bicep curls / rows (so we can see balance).
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Coach's one-paragraph take */}
            {analysis?.total_reps_captured > 0 && analysis.narrative?.detailed_feedback && (
              <div className="hpme-card p-6" data-testid="coach-narrative">
                <div className="hpme-card-corner" />
                <h3 className="font-secondary font-bold text-xl mb-3 flex items-center gap-2">
                  <Target className="w-5 h-5 text-accent-purple" />
                  COACH'S TAKE
                </h3>
                <p className="text-zinc-300 leading-relaxed">{analysis.narrative.detailed_feedback}</p>
              </div>
            )}

            {/* Per-movement cards — one per exercise captured by the camera.
                Score comes from narrative.exercise_breakdown (deterministic blend on backend).
                Technique/Balance/Pace sub-scores + plain-English joint angle rows. */}
            {analysis?.total_reps_captured > 0 && Array.isArray(analysis.narrative?.exercise_breakdown) &&
              analysis.narrative.exercise_breakdown.map((breakdown, eidx) => {
                const exEntry = (analysis.joint_angles || []).find(
                  j => (j.exercise || '').toLowerCase() === (breakdown.exercise || '').toLowerCase()
                );
                const angles = Array.isArray(exEntry?.angles) ? exEntry.angles : [];
                const viewsCaptured = exEntry?.views_captured || {};
                const score = (breakdown.score !== null && breakdown.score !== undefined) ? Math.round(breakdown.score) : null;
                const bk = scoreBucket(score);
                const romN  = breakdown.rom       != null ? Math.round(breakdown.rom)       : null;
                const alignN = breakdown.alignment != null ? Math.round(breakdown.alignment) : null;
                const stabN = breakdown.stability != null ? Math.round(breakdown.stability) : null;
                const tempoN = breakdown.tempo     != null ? Math.round(breakdown.tempo)     : null;
                const romBk   = scoreBucket(romN);
                const alignBk = scoreBucket(alignN);
                const stabBk  = scoreBucket(stabN);
                const tempoBk = scoreBucket(tempoN);
                const recommendedView = breakdown.recommended_view;
                const usedRecommended = recommendedView && Object.keys(viewsCaptured || {}).includes(recommendedView);

                // Each angle now carries the view it was measured from (the
                // backend pre-filtered to the highest-priority view per metric).
                // We render every angle returned. The hint below tells the user
                // which view they could film next time to unlock metrics that
                // had zero eligible reps.
                const visibleAngles = angles;
                const labelsCovered = new Set(angles.map(a => (a.label || '').toLowerCase()));
                // Build "missing" hints from registry — only for metrics that
                // would naturally apply to this exercise but had no eligible
                // sample. We use what's NOT in `labelsCovered` from the
                // standard rows backend would have emitted.
                const nothingFromFront = !Object.keys(viewsCaptured || {}).includes('front');
                const nothingFromSide  = !Object.keys(viewsCaptured || {}).includes('side');
                const hiddenHints = [];
                if (nothingFromFront) hiddenHints.push('symmetry');
                if (nothingFromSide)  hiddenHints.push('depth angles');
                const hiddenByView = hiddenHints;

                return (
                  <div
                    key={eidx}
                    className="hpme-card p-6 border-l-4"
                    style={{ borderLeftColor: score !== null ? toneHex(bk.tone) : '#3f3f46' }}
                    data-testid={`exercise-card-${eidx}`}
                  >
                    <div className="hpme-card-corner" />

                    {/* Header: movement name + big verdict */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Dumbbell className="w-5 h-5 text-primary" />
                        <h3 className="font-secondary font-bold text-xl">{breakdown.exercise}</h3>
                      </div>
                      <span className={`font-secondary font-bold ${toneClass(bk.tone)}`} data-testid={`exercise-tag-${eidx}`}>
                        {bk.label}
                        {score !== null && (
                          <>
                            {' '}
                            <span className="text-zinc-400 font-mono text-sm ml-2">{score}/100</span>
                          </>
                        )}
                      </span>
                    </div>

                    {/* Views-captured badge — shows every view the user filmed
                        from with its rep count. Multi-view sessions get full
                        credit instead of being collapsed into one. */}
                    {Object.keys(viewsCaptured).length > 0 && (
                      <div className="flex items-center gap-1.5 mb-3 text-xs text-zinc-500 flex-wrap" data-testid={`exercise-views-captured-${eidx}`}>
                        <Camera className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="text-zinc-500">Filmed from:</span>
                        {Object.entries(viewsCaptured)
                          .filter(([v]) => v && v !== 'unknown')
                          .map(([v, n], i, arr) => (
                            <span key={v} className="text-zinc-300 font-semibold">
                              {viewLabel(v).replace(' view', '')}{n ? ` (${n})` : ''}
                              {i < arr.length - 1 && <span className="text-zinc-600 font-normal"> · </span>}
                            </span>
                          ))}
                      </div>
                    )}

                    {/* Non-blocking camera-angle suggestion — we still scored what
                        this angle measured; this just unlocks the rest next time. */}
                    {recommendedView && !usedRecommended && (
                      <div className="flex items-start gap-1.5 mb-3 text-xs text-amber-400/90" data-testid={`exercise-view-tip-${eidx}`}>
                        <Camera className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                        <span>
                          Best filmed from the <span className="font-semibold capitalize">{recommendedView}</span> — try it next time to unlock {recommendedView === 'side' ? 'depth & joint angles' : 'full range-of-motion scoring'}.
                        </span>
                      </div>
                    )}

                    {/* Sub-scores: ROM 40% / Alignment 30% / Stability 20% / Tempo 10% */}
                    {score !== null && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3 pb-4 border-b border-zinc-800" data-testid={`exercise-subscores-${eidx}`}>
                        <div title="Range of Motion (40%) — how fully you completed the movement (depth at the bottom or lockout at the top).">
                          <p className="text-[10px] text-zinc-500 uppercase cursor-help flex items-center gap-1">
                            Range <span className="text-zinc-600">ⓘ</span>
                          </p>
                          <p className={`font-semibold text-sm ${toneClass(romBk.tone)}`}>
                            {romN === null ? '—' : romBk.label}
                            {romN !== null && <> <span className="text-zinc-500 font-mono text-xs ml-1">{romN}/100</span></>}
                          </p>
                        </div>
                        <div title="Joint Alignment (30%) — how well your supporting joints (back, hips, knees) tracked through the rep.">
                          <p className="text-[10px] text-zinc-500 uppercase cursor-help flex items-center gap-1">
                            Alignment <span className="text-zinc-600">ⓘ</span>
                          </p>
                          <p className={`font-semibold text-sm ${toneClass(alignBk.tone)}`}>
                            {alignN === null ? '—' : alignBk.label}
                            {alignN !== null && <> <span className="text-zinc-500 font-mono text-xs ml-1">{alignN}/100</span></>}
                          </p>
                        </div>
                        <div title="Stability & Control (20%) — left/right symmetry and how consistent your depth stayed across reps.">
                          <p className="text-[10px] text-zinc-500 uppercase cursor-help flex items-center gap-1">
                            Stability <span className="text-zinc-600">ⓘ</span>
                          </p>
                          <p className={`font-semibold text-sm ${toneClass(stabBk.tone)}`}>
                            {stabN === null ? '—' : stabBk.label}
                            {stabN !== null && <> <span className="text-zinc-500 font-mono text-xs ml-1">{stabN}/100</span></>}
                          </p>
                        </div>
                        <div title="Tempo Consistency (10%) — how even your rep-to-rep pacing was. Steady reps score higher.">
                          <p className="text-[10px] text-zinc-500 uppercase cursor-help flex items-center gap-1">
                            Tempo <span className="text-zinc-600">ⓘ</span>
                          </p>
                          <p className={`font-semibold text-sm ${toneClass(tempoBk.tone)}`}>
                            {tempoN === null ? '—' : tempoBk.label}
                            {tempoN !== null && <> <span className="text-zinc-500 font-mono text-xs ml-1">{tempoN}/100</span></>}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Joint angles — only targeted + view-appropriate ones render. */}
                    {visibleAngles.length > 0 && (
                      <div className="space-y-2 mb-3">
                        {visibleAngles.map((a, aidx) => {
                          const sTone = a.status === 'good' ? 'excellent'
                            : a.status === 'warning' ? 'average'
                            : a.status === 'off' ? 'poor' : 'neutral';
                          const st = angleStatusText(a.status);
                          const desc = jointDescription(a.label, a.phase);
                          const triplet = jointTriplet(a.label);
                          return (
                            <div key={aidx} className="bg-zinc-800/50 p-3 rounded-sm flex items-start gap-4" data-testid={`angle-${eidx}-${aidx}`}>
                              <JointDiagram label={a.label} size={36} />
                              <div className="flex-1 min-w-0">
                                <p className="font-secondary font-bold text-sm uppercase tracking-wide text-zinc-100 flex items-center gap-2 flex-wrap">
                                  <span>{a.label}</span>
                                  {a.phase && a.phase !== 'throughout' && (
                                    <span
                                      className={`text-[10px] font-mono px-1.5 py-0.5 rounded uppercase tracking-wider ${
                                        a.phase === 'at_bottom'
                                          ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                                          : 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                                      }`}
                                      data-testid={`angle-phase-${eidx}-${aidx}`}
                                    >
                                      {a.phase === 'at_bottom' ? 'BOTTOM' : a.phase === 'at_top' ? 'TOP' : a.phase}
                                    </span>
                                  )}
                                  {a.phase === 'throughout' && (
                                    <span
                                      className="text-[10px] font-mono px-1.5 py-0.5 rounded uppercase tracking-wider bg-zinc-700/40 text-zinc-300 border border-zinc-600/40"
                                      data-testid={`angle-phase-${eidx}-${aidx}`}
                                    >
                                      WHOLE REP
                                    </span>
                                  )}
                                  {a.view && (
                                    <span
                                      className={`text-[10px] font-mono px-1.5 py-0.5 rounded uppercase tracking-wider border ${
                                        a.view === 'front'
                                          ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                                          : a.view === 'side'
                                            ? 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                                            : 'bg-zinc-700/40 text-zinc-300 border-zinc-600/40'
                                      }`}
                                      data-testid={`angle-view-${eidx}-${aidx}`}
                                      title={`This row was measured from your ${a.view}-view set${a.sample_count > 1 ? 's' : ''}.`}
                                    >
                                      {a.view.toUpperCase()}
                                    </span>
                                  )}
                                </p>
                                {triplet && (
                                  <p className="text-zinc-300 text-xs mt-0.5" data-testid={`angle-triplet-${eidx}-${aidx}`}>
                                    {triplet}
                                  </p>
                                )}
                                {desc && (
                                  <p className="text-zinc-400 text-xs mt-1 leading-relaxed" data-testid={`angle-desc-${eidx}-${aidx}`}>
                                    {desc}
                                  </p>
                                )}
                                <div className="flex items-baseline gap-3 mt-2 flex-wrap">
                                  <span className="text-xs text-zinc-500">Measured</span>
                                  <span className={`font-mono text-lg ${toneClass(sTone)}`}>{Math.round(a.value)}°</span>
                                  <span className="text-xs text-zinc-500">Ideal</span>
                                  <span className="font-mono text-sm text-zinc-300">{a.ideal_min}–{a.ideal_max}°</span>
                                  {st && <span className={`text-xs ${toneClass(sTone)}`}>· {st}</span>}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Missing-view hint — only when the user filmed from views
                        that physically can't measure certain key metrics. */}
                    {hiddenByView.length > 0 && (() => {
                      const tip = nothingFromFront && nothingFromSide
                        ? 'film one set from the side and one from the front next time to unlock symmetry + depth metrics'
                        : nothingFromFront
                          ? 'film one set from the front next time to unlock symmetry, knee tracking, and elbow flare'
                          : 'film one set from the side next time to unlock joint depth and back angle';
                      return (
                        <div className="flex items-start gap-2 mb-3 text-xs text-zinc-500 bg-zinc-800/30 px-3 py-2 rounded-sm" data-testid={`filtered-by-view-${eidx}`}>
                          <Camera className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                          <span>
                            <span className="text-zinc-400 font-semibold">{hiddenByView.join(' & ')}</span> not captured — {tip}.
                          </span>
                        </div>
                      );
                    })()}

                    {/* Coach's per-movement note + next-session cue */}
                    {breakdown.feedback && (
                      <p className="text-zinc-300 text-sm mb-2">{breakdown.feedback}</p>
                    )}
                    {breakdown.key_issue && (
                      <div className="flex items-start gap-2 mt-2">
                        <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <span className="text-yellow-400 text-sm">{breakdown.key_issue}</span>
                      </div>
                    )}
                    {breakdown.cue && (
                      <div className="mt-3 bg-primary/10 border border-primary/20 px-3 py-1.5 rounded-sm">
                        <span className="text-xs text-zinc-500 uppercase">Next session cue: </span>
                        <span className="text-primary text-sm font-semibold">{breakdown.cue}</span>
                      </div>
                    )}

                    {/* Score formula footer so the math is transparent */}
                    {score !== null && (
                      <p className="text-[10px] text-zinc-600 mt-4 pt-3 border-t border-zinc-800/60">
                        Score = 50% Technique + 30% Balance + 20% Pace
                      </p>
                    )}
                  </div>
                );
              })
            }

            <div className="flex gap-4">
              <Button 
                className="hpme-btn-secondary"
                onClick={() => navigate('/sessions')}
                data-testid="view-sessions-btn"
              >
                View All Sessions
              </Button>
              <Button 
                className="hpme-btn-primary"
                onClick={() => navigate('/workouts')}
                data-testid="new-workout-btn"
              >
                Start New Workout
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default WorkoutSession;
